import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { api, errMsg, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/context/MetaContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SelectField } from "@/components/crm/fields";
import { PageHeader, Skeletons } from "@/components/crm/bits";

function SortTable({ rows, cols, testid }) {
  const [sort, setSort] = useState([cols[1][0], -1]);
  const sorted = [...rows].sort((a, b) => (a[sort[0]] > b[sort[0]] ? 1 : -1) * sort[1]);
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-gray-500 border-b border-gray-100">
          {cols.map(([k, l]) => <th key={k} className="py-2 pr-4 font-medium cursor-pointer select-none whitespace-nowrap" onClick={() => setSort([k, sort[0] === k ? -sort[1] : -1])}>{l}{sort[0] === k ? (sort[1] > 0 ? " ↑" : " ↓") : ""}</th>)}
        </tr></thead>
        <tbody>{sorted.map((r) => <tr key={r.name} className="border-b border-gray-50 last:border-0">{cols.map(([k]) => <td key={k} className={`py-2.5 pr-4 ${k === "name" ? "text-gray-800" : "font-mono-num text-gray-600"}`}>{r[k]}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

const Stat = ({ label, value, testid }) => (
  <div className="card-surface p-4" data-testid={testid}><p className="text-xs text-gray-500">{label}</p><p className="font-display text-2xl font-bold mt-1.5">{value}</p></div>
);

export default function Reports() {
  const { can } = useAuth();
  const { activeUsers } = useMeta();
  const [f, setF] = useState({ date_from: "", date_to: "", owner_id: "" });
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    const p = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/reports", { params: p }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [f]);
  const t = d?.totals;
  const axis = { fontSize: 11, fill: "#6B7280" };
  return (
    <div className="fade-in space-y-5" data-testid="reports-page">
      <PageHeader title="Reports" subtitle="Lead generation, conversion and team accountability.">
        <Input type="date" className="h-9 w-[150px]" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="report-date-from" />
        <span className="text-gray-400 text-sm">to</span>
        <Input type="date" className="h-9 w-[150px]" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="report-date-to" />
        {can("reports:team") && <div className="w-44"><SelectField testid="report-owner-select" value={f.owner_id} onChange={(v) => setF({ ...f, owner_id: v })} options={activeUsers.map((u) => [u.user_id, u.name])} placeholder="All employees" /></div>}
        <Button variant="outline" className="h-9" data-testid="report-export-button" onClick={() => downloadFile("/reports/export", "thakker-one-report.csv", Object.fromEntries(Object.entries(f).filter(([, v]) => v))).catch((e) => toast.error(errMsg(e)))}><Download className="h-4 w-4 mr-1.5" />Export CSV</Button>
      </PageHeader>
      {!d ? <Skeletons n={5} /> : (<>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Stat testid="report-total" label="Total leads" value={t.total} />
          <Stat testid="report-qualified" label="Qualified" value={t.qualified} />
          <Stat testid="report-won" label="Won" value={t.won} />
          <Stat testid="report-lost" label="Lost" value={t.lost} />
          <Stat testid="report-conversion" label="Conversion rate" value={`${t.conversion_rate}%`} />
          <Stat testid="report-inactive" label="Inactive 5+ days" value={t.inactive_over_limit} />
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          <section className="card-surface p-5" data-testid="chart-monthly">
            <h3 className="font-display text-[15px] font-semibold mb-4">Monthly lead generation & conversion</h3>
            <div className="h-64"><ResponsiveContainer><BarChart data={d.monthly} barGap={2}><CartesianGrid vertical={false} stroke="#F3F4F6" /><XAxis dataKey="month" tick={axis} axisLine={false} tickLine={false} /><YAxis tick={axis} axisLine={false} tickLine={false} allowDecimals={false} width={28} /><Tooltip cursor={{ fill: "#F6F8F6" }} /><Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} /><Bar dataKey="created" name="New leads" fill="#0B3C2D" radius={[3, 3, 0, 0]} /><Bar dataKey="won" name="Won" fill="#6EE7B7" radius={[3, 3, 0, 0]} /></BarChart></ResponsiveContainer></div>
          </section>
          <section className="card-surface p-5" data-testid="chart-stage">
            <h3 className="font-display text-[15px] font-semibold mb-4">Leads by pipeline stage</h3>
            <div className="h-64"><ResponsiveContainer><BarChart data={d.by_stage} layout="vertical" margin={{ left: 10 }}><XAxis type="number" hide allowDecimals={false} /><YAxis type="category" dataKey="name" tick={axis} width={150} interval={0} axisLine={false} tickLine={false} /><Tooltip cursor={{ fill: "#F6F8F6" }} /><Bar dataKey="count" name="Leads" fill="#0B3C2D" radius={[0, 3, 3, 0]} barSize={12} /></BarChart></ResponsiveContainer></div>
          </section>
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          <section className="card-surface p-5"><h3 className="font-display text-[15px] font-semibold mb-3">By source</h3><SortTable testid="table-by-source" rows={d.by_source} cols={[["name", "Source"], ["count", "Leads"], ["won", "Won"]]} /></section>
          <section className="card-surface p-5"><h3 className="font-display text-[15px] font-semibold mb-3">By industry</h3><SortTable testid="table-by-industry" rows={d.by_industry} cols={[["name", "Industry"], ["count", "Leads"], ["won", "Won"]]} /></section>
        </div>
        <section className="card-surface p-5"><h3 className="font-display text-[15px] font-semibold mb-3">Lead ownership & employee activity</h3>
          <SortTable testid="table-by-owner" rows={d.by_owner} cols={[["name", "Employee"], ["leads", "Leads"], ["active", "Active"], ["won", "Won"], ["overdue", "Overdue"], ["activities", "Activities"]]} /></section>
      </>)}
    </div>
  );
}
